import {
  doGetUserSubscription,
  doGetPlans,
  doGetPaymentIntent,
  doPostConfirmSubscription,
  doPostConfirmSingleCharge,
  doGetIfLifetimeIsActive,
  doPostConfirmDowngradeSubscription,
  doGetSubscriptionChangePreview,
  doPostConfirmFreeSubscription,
  doGetDiscountByPromoCode,
  doGetReceipt,
  doGetProfessionalReceiptDetails,
  doGetLifetimeReceiptDetails,
} from '@/api/subscriptions';
import { isSuccessfulResponse } from '@/api/utilities';
import {
  getFeatureFlagProvider,
  MILESTONE_PLANS_UPDATES_PART_ONE_ENABLED,
  MILESTONE_FREE_AND_LIFETIME_UPDATES_V2_ENABLED,
} from '@/lib/featureFlag';

const featureFlagProvider = getFeatureFlagProvider();

// TODO: move to constants
export const PlanNames = {
  FREE: 'Free Trial',
  STANDARD: 'Standard',
  PRO: 'Pro',
  PROFESSIONAL: 'Professional',
  LIFETIME: 'Lifetime',
};

const milestoneFreeAndLifetimeUpdatesV2Enabled = featureFlagProvider.isFeatureEnabled(
  MILESTONE_FREE_AND_LIFETIME_UPDATES_V2_ENABLED,
);

if (milestoneFreeAndLifetimeUpdatesV2Enabled) {
  PlanNames['FREE'] = 'Free';
}

const PAST_DUE_STATUS = 'past_due';

export default {
  namespaced: true,
  state: () => ({
    userFreePlan: false,
    userSubscription: null,
    attemptToLoadSubscriptionDone: false,
    plans: null,
  }),
  mutations: {
    setUserFreePlan(state, data) {
      state.userFreePlan = data;
    },
    setUserSubscription(state, userSubscription) {
      state.userSubscription = userSubscription;
    },
    setAttemptToLoadSubscriptionDone(state) {
      state.attemptToLoadSubscriptionDone = true;
    },
    setPlans(state, plans) {

      // Note: when removing this FF remember to put this array of benefits and limitations in the DB and delete this in case the
      // updated seeder is not execute
      if (milestoneFreeAndLifetimeUpdatesV2Enabled) {
        plans?.map((plan) => {
          if (plan.isFree) {
            plan.benefits = [
              "<span class=\"text text-l-small-bold\">MOC credit</span> available for participating medical boards",
              "Access to all articles",
              "Access to certifications for Federal and State training requirements"
            ];

            plan.limitations = [
              "Access to all Premium Courses",
              "Access to all special topics, such as DEA MATE ACT training"
            ];
          }
          if (plan.title === PlanNames.STANDARD) {
            plan.benefits = [
              "<span class=\"text text-l-small-bold\">MOC credit</span> available for participating medical boards",
              "Access to all articles",
              "Access to all certifications for Federal and State training requirements",
              "Access to all Premium Courses",
              "Access to all special topics, such as DEA MATE ACT training"
            ];
          };
          if (plan.title === PlanNames.LIFETIME) {
            plan.benefits = [
              "<span class=\"text text-l-small-bold\">UNLIMITED MOC credit</span> for participating medical boards",
              "<span class=\"text text-l-small-bold\">UNLIMITED</span> articles",
              "<span class=\"text text-l-small-bold\">UNLIMITED</span> certifications for Federal and State training requirements",
              "<span class=\"text text-l-small-bold\">UNLIMITED</span> Premium Courses",
              "<span class=\"text text-l-small-bold\">ONE-TIME</span> charge and <span class=\"text text-l-small-bold\">never worry</span> about CME again!"
            ];
          }
        });
      }

      state.plans = plans;
    },
  },
  actions: {
    async getUserSubscription({ commit }) {
      const response = await doGetUserSubscription();
      if (response.data) {
        commit('setUserSubscription', response.data);
      }

      commit('setAttemptToLoadSubscriptionDone');

      return response;
    },
    async removeUserSubscription({ commit }) {
      commit('setUserSubscription', null);
    },
    async getPlans({ commit, getters }) {
      if (getters.plans) return getters.plans;

      const plans = await doGetPlans();
      commit('setPlans', plans);

      return plans;
    },
    getPaymentIntent() {
      return doGetPaymentIntent();
    },
    postConfirmSubscription(context, payload) {
      return doPostConfirmSubscription(payload);
    },
    postConfirmSingleCharge(context, payload) {
      return doPostConfirmSingleCharge(payload);
    },
    getIfLifetimeIsActive(context) {
      return doGetIfLifetimeIsActive();
    },
    async postConfirmDowngradeSubscription(context, payload) {
      let response = await doPostConfirmDowngradeSubscription(payload);

      if (isSuccessfulResponse(response)) {
        return response.data;
      }

      return response.error;
    },
    async getSubscriptionChangePreview(context, payload) {
      let response = await doGetSubscriptionChangePreview(payload);

      if (isSuccessfulResponse(response)) {
        return response.data;
      }

      return response.error;
    },
    setIsUserFreePlan(context, data) {
      context.commit('setUserFreePlan', data);
    },
    postConfirmFreeSubscription(context, payload) {
      return doPostConfirmFreeSubscription(payload);
    },
    getDiscountByPromoCode(context, payload) {
      return doGetDiscountByPromoCode(payload);
    },
    getReceipt(context, payload) {
      return doGetReceipt(payload);
    },
    getProfessionalReceiptDetails(context, payload) {
      return doGetProfessionalReceiptDetails(payload);
    },
    getLifetimeReceiptDetails(context, payload) {
      return doGetLifetimeReceiptDetails(payload);
    },
    loadSubscriptionDone(context) {
      context.commit('setAttemptToLoadSubscriptionDone');
    },
  },
  getters: {
    canUpgradeSubscription: (
      state,
      { hasProfessionalSubscription, hasStandardSubscription, hasLifetimeSubscription },
    ) => {
      const milestonePlansUpdatedPartOneEnabled = getFeatureFlagProvider().isFeatureEnabled(
        MILESTONE_PLANS_UPDATES_PART_ONE_ENABLED,
      );

      if (hasStandardSubscription && milestonePlansUpdatedPartOneEnabled) return false;
      if (hasProfessionalSubscription) return false;
      if (hasLifetimeSubscription) return false;

      return true;
    },
    // TODO: this value can be undefined if the list of plans is not loaded
    // we should improve this that could be prone to errors
    currentPlan: (state, { freePlan, hasActiveSubscription, plans }) => {
      if (!hasActiveSubscription) return freePlan;

      return plans?.find((plan) => {
        return plan.title === state.userSubscription.planName;
      });
    },
    currentPlanName: (state, { currentPlan }) => {
      return currentPlan?.title;
    },
    /**
     * @deprecated - use hasFreeSubscription instead
     */
    isUserFreePlan: (state) => {
      return state.userFreePlan;
    },
    hasActiveSubscription: (state) => {
      return state.userSubscription?.hasActivePaidSubscription;
    },
    /**
     * @deprecated - use hasFreeSubscription instead
     */
    hasFreeTrial: (state, getters) => {
      return !getters.hasActiveSubscription;
    },
    getAttemptToLoadSubscriptionDone: (state) => {
      return state.attemptToLoadSubscriptionDone;
    },
    hasFreeSubscription: (state, { hasActiveSubscription }) => {
      return !hasActiveSubscription;
    },
    hasLifetimeSubscription: (state) => {
      return !!state.userSubscription?.hasLifetimeSubscription;
    },
    hasProSubscription: (state, { hasActiveSubscription }) => {
      if (!hasActiveSubscription) return false;

      return state.userSubscription?.planName === PlanNames.PRO;
    },
    hasProfessionalSubscription: (state, { hasActiveSubscription }) => {
      if (!hasActiveSubscription) return false;

      return state.userSubscription?.planName === PlanNames.PROFESSIONAL;
    },
    hasStandardSubscription: (state, { hasActiveSubscription }) => {
      if (!hasActiveSubscription) return false;

      return state.userSubscription?.planName === PlanNames.STANDARD;
    },
    isSubscriptionInGracePeriod: (state) => {
      return !!state.userSubscription?.cancelledAt;
    },
    isSubscriptionInPastDueStatus: (state) => {
      return state.userSubscription?.status === PAST_DUE_STATUS;
    },
    plans: (state) => {
      return state.plans;
    },
    subscriptionEndDate: (state) => {
      return state.userSubscription?.endAt;
    },
    availablePlansToChange: (
      state,
      { canUpgradeSubscription, currentPlanName, freePlan, hasActiveSubscription, plans },
    ) => {
      const milestonePlansUpdatedPartOneEnabled = getFeatureFlagProvider().isFeatureEnabled(
        MILESTONE_PLANS_UPDATES_PART_ONE_ENABLED,
      );

      if (canUpgradeSubscription) {
        let plansToShow = plans;

        if (milestoneFreeAndLifetimeUpdatesV2Enabled && hasActiveSubscription) {
          // upgrade of lifetime plan is not allowed until we work on the feature
          plansToShow = plans.filter((plan) => plan.title !== PlanNames.LIFETIME) ?? [];
        }

        if (milestonePlansUpdatedPartOneEnabled) {
          plansToShow = plans.filter((plan) => plan.title !== PlanNames.PROFESSIONAL) ?? [];
        }

        return plansToShow.filter((plan) => plan.title !== currentPlanName);
      } else {
        return [freePlan];
      }
    },
    // TODO: move to a plans store
    freePlan: (state, { plans }) => {
      return plans?.find((plan) => {
        return plan.isFree;
      });
    },
    standardPlan: (state, { plans }) => {
      return plans?.find((plan) => {
        return plan.title === PlanNames.STANDARD;
      });
    },
    proPlan: (state, { plans }) => {
      return plans?.find((plan) => {
        return plan.title === PlanNames.PRO;
      });
    },
    professionalPlan: (state, { plans }) => {
      return plans?.find((plan) => {
        return plan.title === PlanNames.PROFESSIONAL;
      });
    },
    lifetimePlan: (state, { plans }) => {
      return plans?.find((plan) => {
        return plan.title === PlanNames.LIFETIME;
      });
    },
  },
};
